// src/redux/slice/fuelCardSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { encryptData } from "../../utils/localEncruption";
import { decryptData, decryptAPIData } from "../../utils/localEncruption";

// Fetch and decrypt fuel cards data from API
const fetchFuelCardsFromAPI = async () => {
  const response = await fetch(process.env.REACT_APP_FUEL_CC_URL);
  const encryptedData = await response.text();
  try {
    const decryptedData = await decryptAPIData(
      encryptedData,
      `${process.env.REACT_APP_CREDIT_CARD_SALT}`,
    );
    return decryptedData;
  } catch (error) {
    console.error("Decryption failed for fuel card data:", error);
  }
  return response.json();
};

// localStorage helpers
const getFuelCardsFromLocalStorage = () => {
  const storedData = localStorage.getItem("fuelCardsData");
  const salt = `${process.env.REACT_APP_SALT}`;
  if (!storedData) return null;
  try {
    return decryptData(storedData, salt);
  } catch (error) {
    return null;
  }
};

const saveFuelCardsToLocalStorage = (data) => {
  const salt = `${process.env.REACT_APP_SALT}`;
  const encryptedData = encryptData(data, salt);
  localStorage.setItem("fuelCardsData", encryptedData);
};

const getTimestamp = (key) => localStorage.getItem(`${key}_timestamp`);
const saveTimestamp = (key) =>
  localStorage.setItem(`${key}_timestamp`, new Date().getTime());

const DATA_FRESHNESS_THRESHOLD = 6 * 60 * 60 * 1000; // 6 hours

// Async thunk
export const fetchFuelCards = createAsyncThunk(
  "fuelCard/fetchFuelCards",
  async (_, thunkAPI) => {
    const cachedData = getFuelCardsFromLocalStorage();
    const timestamp = getTimestamp("fuelCardsData");
    const currentTime = new Date().getTime();
    const isDataStale =
      !timestamp || currentTime - timestamp > DATA_FRESHNESS_THRESHOLD;

    if (!cachedData || isDataStale) {
      try {
        const data = await fetchFuelCardsFromAPI();
        saveFuelCardsToLocalStorage(data);
        saveTimestamp("fuelCardsData");
        return data;
      } catch (error) {
        return thunkAPI.rejectWithValue(error.message);
      }
    }
    return cachedData;
  },
);

const initialState = {
  fuelCards: getFuelCardsFromLocalStorage() || [],
  loading: "idle",
  error: null,
  expandedCard: null,
  sortConfig: { key: "annual_fee", direction: "asc" },
  selectedBank: null,
};

const fuelCardSlice = createSlice({
  name: "fuelCard",
  initialState,
  reducers: {
    toggleCardExpansion(state, action) {
      state.expandedCard =
        state.expandedCard === action.payload ? null : action.payload;
    },
    setSortConfig(state, action) {
      state.sortConfig = {
        key: action.payload.key,
        direction: action.payload.direction,
      };
    },
    setSelectedBank(state, action) {
      state.selectedBank = action.payload;
    },
    clearFilters(state) {
      state.selectedBank = null;
      state.sortConfig = { key: "annual_fee", direction: "asc" };
      state.expandedCard = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchFuelCards.pending, (state) => {
        state.loading = "pending";
        state.error = null;
      })
      .addCase(fetchFuelCards.fulfilled, (state, action) => {
        state.loading = "idle";
        state.fuelCards = action.payload;
      })
      .addCase(fetchFuelCards.rejected, (state, action) => {
        state.loading = "idle";
        state.error = action.payload;
      });
  },
});

export const {
  toggleCardExpansion,
  setSortConfig,
  setSelectedBank,
  clearFilters,
} = fuelCardSlice.actions;

export default fuelCardSlice.reducer;
